<script setup lang="ts">
import {
  EnumerationEvaluationOptionDto,
  EnumerationEvaluationResultDto,
  EvaluationParameterDto,
  instanceOfEnumerationEvaluationParameterDto,
  instanceOfEnumerationEvaluationResultDto,
  NumericEvaluationResultDto,
  QuantityValueType,
} from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { toDisplayValue } from '@/utils/numberUtils';
import { I18nKey } from '@/utils/typings';
import { mdiAccountEdit, mdiClose, mdiPencil } from '@mdi/js';
import { computed, ref } from 'vue';
import EnumInput from './manual/EnumInput.vue';
import NumericInput from './manual/NumericInput.vue';

type Deviation = {
  value?: number;
  unitAbbreviation?: string | null;
  decimalPlaces?: number | null;
  valueType?: QuantityValueType | null;
};

const props = defineProps<{
  evaluation: EvaluationParameterDto;
  deviation: Deviation;
  result?: NumericEvaluationResultDto | EnumerationEvaluationResultDto;
  hasValue: boolean;
  editable?: boolean;
  disabled?: boolean;
  submitting?: boolean;
}>();

const emits = defineEmits<{
  input: [value: number | EnumerationEvaluationOptionDto | null];
}>();

const isEditing = ref(false);

// Without a stored value there is nothing to display, so the input shows right away.
const showInput = computed(
  () => !!props.editable && !props.disabled && (!props.hasValue || isEditing.value),
);

const enumerationResult = computed(() =>
  props.result && instanceOfEnumerationEvaluationResultDto(props.result) ? props.result : undefined,
);

function closeEditing() {
  isEditing.value = false;
}

function selectOption(option: EnumerationEvaluationOptionDto) {
  emits('input', option);
  closeEditing();
}
</script>

<template>
  <div v-if="showInput" class="d-flex align-center">
    <EnumInput
      v-if="instanceOfEnumerationEvaluationParameterDto(evaluation)"
      :evaluation="evaluation"
      :result="enumerationResult"
      :disabled="submitting"
      @input="selectOption"
    />

    <NumericInput
      v-else
      :evaluation="evaluation"
      :value="deviation.value"
      :disabled="submitting"
      :formatValue="{ fractionDigits: deviation.decimalPlaces, valueType: deviation.valueType }"
      allowClear
      @input="(value) => emits('input', value)"
      @blur="closeEditing"
      @cancel="closeEditing"
    />

    <Tooltip v-if="hasValue">
      <template #target="{ props: targetProps }">
        <Button
          v-bind="targetProps"
          variant="text"
          size="small"
          :icon="mdiClose"
          :aria-label="$t('Cancel')"
          tabindex="-1"
          class="ml-1"
          @mousedown.prevent
          @click="closeEditing"
        />
      </template>
      <template #tooltip>{{ $t('Cancel') }}</template>
    </Tooltip>
  </div>

  <div v-else class="d-flex align-center">
    <span v-if="instanceOfEnumerationEvaluationParameterDto(evaluation)">{{
      enumerationResult?.selectedOption?.name ?? EMPTY_FIELD_SIGN
    }}</span>
    <span v-else>{{
      toDisplayValue(deviation.value, {
        fractionDigits: deviation.decimalPlaces,
        valueType: deviation.valueType,
        unitAbbreviation: deviation.unitAbbreviation,
      })
    }}</span>

    <!-- The result has no "entered by hand" flag; only unmapped (editable) evaluations get one. -->
    <Tooltip v-if="editable && hasValue">
      <template #target="{ props: targetProps }">
        <v-icon v-bind="targetProps" :icon="mdiAccountEdit" size="small" class="ml-1" />
      </template>
      <template #tooltip>{{ $t('ManuallyEnteredValue' as I18nKey) }}</template>
    </Tooltip>

    <Button
      v-if="editable && !disabled"
      variant="text"
      size="small"
      :icon="mdiPencil"
      :aria-label="$t('EnterValue')"
      class="ml-1"
      @click="isEditing = true"
    />
  </div>
</template>
